/**
 * <Stack> — vertical flex container.
 *
 * The column counterpart of <Row>. Same props; `align` defaults to `stretch`
 * so children fill the cross-axis width by default (the usual column intent).
 */
import React from "react"
import { View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SpacingKey } from "../../../theme/tokens/spacing"
import { alignMap, justifyMap, type Align, type Justify } from "../layout"

export type StackProps = {
    children?: React.ReactNode
    /** Space between children — spacing token, defaults to `"2"` (8px). */
    gap?: SpacingKey
    /** Main-axis distribution — defaults to `start`. */
    justify?: Justify
    /** Cross-axis alignment — defaults to `stretch`. */
    align?: Align
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function Stack(props: StackProps) {
    const { children, gap = "2", justify = "start", align = "stretch", style, testID } = props

    return (
        <View testID={testID} style={[styles.container(gap, justify, align), style]}>
            {children}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    container: (gap: SpacingKey, justify: Justify, align: Align) => ({
        flexDirection: "column" as const,
        gap: theme.spacing[gap],
        justifyContent: justifyMap[justify],
        alignItems: alignMap[align],
    }),
}))
